import React, { useMemo, useState } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SubscriptionDialog, { DialogButton, DialogNotice } from '../../../components/subscription/SubscriptionDialog';
import PlanFitGroup from './PlanFitGroup';
import PlanChangeCreditValues from './PlanChangeCreditValues';
import usePlanChangeSimulation from './usePlanChangeSimulation';
import planStyles from './PlanChangeModal.module.css';

/**
 * Textos por tipo de item que pode passar do limite do plano.
 * As chaves são as mesmas de subscription/company/plan_fit.php (kinds).
 */
const KIND_TEXTS = {
  resources: {
    title: 'Agendas',
    one: 'agenda',
    many: 'agendas',
    hint: 'As não marcadas ficam inativas. Horários e histórico continuam guardados.',
  },
  users: {
    title: 'Usuários',
    one: 'usuário',
    many: 'usuários',
    hint: 'Quem não ficar marcado perde o acesso à conta e convites não marcados são cancelados. O cadastro continua e o acesso pode ser devolvido em Usuários.',
  },
  rooms: {
    title: 'Salas',
    one: 'sala',
    many: 'salas',
    hint: 'As salas não marcadas ficam inativas.',
  },
  procedures: {
    title: 'Procedimentos',
    one: 'procedimento',
    many: 'procedimentos',
    hint: 'Os procedimentos não marcados ficam inativos e saem da lista de agendamento.',
  },
};

const KIND_ORDER = ['resources', 'users', 'rooms', 'procedures'];

// Chave única de um item dentro de um tipo (usuários misturam pessoas e convites)
const itemKey = (item) => `${item.type || 'item'}:${item.id}`;

/**
 * Seleção inicial: quem não pode sair (responsável pela conta, quem está trocando),
 * as agendas ligadas a essas pessoas e as salas mais usadas já vêm marcados.
 */
function buildInitialSelection(kinds) {
  const selection = {};
  KIND_ORDER.forEach((kind) => {
    const info = kinds?.[kind];
    if (!info || !info.over) return;
    const preselected = info.items
      .filter((item) => item.locked || item.suggested)
      .slice(0, info.limit)
      .map(itemKey);
    selection[kind] = new Set(preselected);
  });
  return selection;
}

/**
 * Passo de "escolha o que fica" antes do checkout de um plano menor que o uso
 * atual da clínica (caso típico: trial com 2 profissionais e 3 usuários indo para
 * o Essencial). Mesma casca do UpgradePlanModal (SubscriptionDialog).
 *
 * Props:
 *   fit   — resposta de GetPlanFitAPI ({ plan, fits, can_fix, blocked_reason, kinds })
 *   onConfirm(keep) — keep = { resources: [ids], users: [userIds], invitations: [ids], rooms: [ids], procedures: [ids] }
 *   subtitle / confirmLabel / loadingLabel — textos opcionais (troca de quem já assina,
 *     que não passa pelo pagamento); sem eles, os textos do checkout.
 *   showCreditNote — troca de quem já assina: mostra o crédito e a próxima cobrança.
 */
const PlanFitModal = ({
  open,
  fit,
  loading = false,
  error = null,
  onConfirm,
  onCancel,
  subtitle = 'Sua conta usa mais do que este plano permite. Marque o que continua ativo; o resto só fica inativo quando o pagamento for confirmado. Se você desistir, nada muda e nada é apagado.',
  confirmLabel = 'Continuar para o pagamento',
  loadingLabel = 'Abrindo o pagamento...',
  showCreditNote = false,
}) => {
  const kinds = fit?.kinds;
  const [selection, setSelection] = useState(() => buildInitialSelection(kinds));
  const creditPreview = usePlanChangeSimulation(fit?.plan?.id, !!open && showCreditNote && fit?.can_fix !== false);

  const overKinds = useMemo(
    () => KIND_ORDER.filter((kind) => kinds?.[kind]?.over),
    [kinds]
  );

  if (!open || !fit) return null;

  const planName = fit.plan?.name || 'escolhido';
  const canFix = fit.can_fix !== false;

  // Quem não pode escolher este plano (ex.: administrador indo para um plano de 1 usuário):
  // explica a regra sem tom de erro e sem prometer a escolha do que fica.
  const usersLimit = kinds?.users?.limit;
  const blockedText = Number.isFinite(Number(usersLimit)) && usersLimit !== null
    ? (Number(usersLimit) === 1
      ? 'Este plano tem 1 usuário, que fica com a pessoa responsável pela conta. Por isso, só ela pode escolher este plano.'
      : `Este plano tem ${usersLimit} usuários e um deles fica com a pessoa responsável pela conta. Por isso, só ela pode escolher este plano.`)
    : fit.blocked_reason;

  const selectedCount = (kind) => (selection[kind] ? selection[kind].size : 0);
  const isComplete = overKinds.every((kind) => selectedCount(kind) === kinds[kind].limit);

  const toggle = (kind, item) => {
    if (item.locked || loading) return;
    const key = itemKey(item);
    setSelection((prev) => {
      const next = new Set(prev[kind] || []);
      if (next.has(key)) {
        next.delete(key);
      } else if (next.size < kinds[kind].limit) {
        next.add(key);
      }
      return { ...prev, [kind]: next };
    });
  };

  const handleConfirm = () => {
    if (!isComplete || loading) return;
    const keep = { resources: [], users: [], invitations: [], rooms: [], procedures: [] };
    overKinds.forEach((kind) => {
      kinds[kind].items.forEach((item) => {
        if (!selection[kind]?.has(itemKey(item))) return;
        if (kind === 'users') {
          (item.type === 'invitation' ? keep.invitations : keep.users).push(item.id);
        } else {
          keep[kind].push(item.id);
        }
      });
    });
    onConfirm(keep);
  };

  const formatCount = (kind, n) => `${n} ${n === 1 ? KIND_TEXTS[kind].one : KIND_TEXTS[kind].many}`;

  return (
    <SubscriptionDialog
      open
      onClose={onCancel}
      locked={loading}
      maxWidth={640}
      title={canFix
        ? <>Escolha o que fica no plano <span className={planStyles.titleHighlight}>{planName}</span></>
        : <>Plano <span className={planStyles.titleHighlight}>{planName}</span></>}
      subtitle={canFix ? 'Marque em cada grupo o que continua ativo.' : blockedText}
      secondaryAction={canFix && (
        <DialogButton variant="text" onClick={onCancel} disabled={loading}>Voltar</DialogButton>
      )}
      actions={canFix ? (
        <DialogButton onClick={handleConfirm} disabled={!isComplete} loading={loading} loadingLabel={loadingLabel}>
          {confirmLabel}
        </DialogButton>
      ) : (
        <DialogButton onClick={onCancel}>Entendi</DialogButton>
      )}
    >
      {canFix && (
        <DialogNotice tone="warning" icon={<InfoOutlinedIcon fontSize="inherit" />}>
          {subtitle}
        </DialogNotice>
      )}

      {canFix && overKinds.map((kind) => (
        <PlanFitGroup
          key={kind}
          title={KIND_TEXTS[kind].title}
          instruction={`Escolha ${formatCount(kind, kinds[kind].limit)} de ${kinds[kind].count}`}
          hint={KIND_TEXTS[kind].hint}
          items={kinds[kind].items}
          limit={kinds[kind].limit}
          selected={selection[kind]}
          itemKey={itemKey}
          disabled={loading}
          onToggle={(item) => toggle(kind, item)}
        />
      ))}

      {canFix && showCreditNote && <PlanChangeCreditValues {...creditPreview} />}

      {error && (
        <DialogNotice tone="error" icon={<ErrorOutlineIcon fontSize="inherit" />}>
          {error}
        </DialogNotice>
      )}
    </SubscriptionDialog>
  );
};

export default PlanFitModal;
